import { Box, Button, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import CustomAutocompleteMulti from "../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import IconWrapper from "../../../common/components/functional/icons/IconWrapper";
import { ReactComponent as ContinueIcon } from "../../../common/icons/arrows-right-filled.svg";
import { AutocompleteType } from "../../../common/types/autoComplete";
import useLanguageSkills from "../../../hooks/useLanguageSkills";
import { OrderRowDisplayTaskSpec, OrderType } from "../orderAPI";
import { format, isSameDay, isSameYear, isValid } from "date-fns";
import { fi } from "date-fns/locale";
import {
  dateTimeFormat,
  hoursMinutesFormat,
  leadingZerosDateFormat,
  leadingZerosDayMonthFormat,
} from "../../../common/helpers/formatDateTime";
import { PopperActionBannerContainer } from "../../../common/components/functional/hoverPopper/styled";

interface Props {
  taskSpec: OrderRowDisplayTaskSpec;
  title: string;
  workStarts: string;
  workEnds?: string;
  type: OrderType;
  onClick: () => void;
}

export const OrderRowPopperContent = ({
  taskSpec,
  title,
  workStarts,
  workEnds,
  type,
  onClick,
}: Props) => {
  const { t } = useTranslation(["order", "common", "languageSkill"]);
  const { getLanguageSkillsNameByCode } = useLanguageSkills();
  const { skill, certificate, training } = useAppSelector(
    (state) => state.localized,
  );

  const formatSkillsForAutocomplete = (
    skillIds: string[],
  ): AutocompleteType[] =>
    skillIds.map((id: string) => ({ id, name: skill[id] || id }));

  const formatCertificatesForAutocomplete = (
    certificateIds: string[],
  ): AutocompleteType[] =>
    certificateIds.map((id: string) => ({ id, name: certificate[id] || id }));

  const formatTrainingsForAutocomplete = (
    trainings: string[],
  ): AutocompleteType[] =>
    trainings.map((id: string) => ({ id, name: training[id] || id }));

  const formatOrderRowDateTime = () => {
    const startDate = new Date(workStarts);
    const endDate = workEnds ? new Date(workEnds) : null;

    if (!isValid(startDate)) return "";
    const formattedStartDateLong = format(startDate, leadingZerosDateFormat, {
      locale: fi,
    });
    const formattedStartDateShort = format(
      startDate,
      leadingZerosDayMonthFormat,
      { locale: fi },
    );

    if (type === OrderType.Short && endDate && isValid(endDate)) {
      const startDateTime = format(startDate, dateTimeFormat, {
        locale: fi,
      });
      if (isSameDay(startDate, endDate)) {
        const endTime = format(endDate, hoursMinutesFormat, { locale: fi });
        return `${startDateTime} - ${endTime}`;
      } else {
        const endDateTime = format(endDate, dateTimeFormat, { locale: fi });
        return `${startDateTime} - ${endDateTime}`;
      }
    }

    if (type === OrderType.FixedTerm && endDate && isValid(endDate)) {
      const endDateFormatted = format(endDate, leadingZerosDateFormat, {
        locale: fi,
      });

      if (isSameYear(startDate, endDate)) {
        return `${formattedStartDateShort} - ${endDateFormatted}`;
      } else {
        return `${formattedStartDateLong} - ${endDateFormatted}`;
      }
    }

    return formattedStartDateLong;
  };

  return (
    <>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          bgcolor: (theme) => theme.palette.background.default,
          p: "8px 16px 16px 16px",
        }}
      >
        <Box display="flex" alignItems="center" gap="32px">
          <Typography
            variant="header"
            fontWeight={600}
            fontSize="28px"
            pb="8px"
          >
            {title}
          </Typography>
          <Typography>{formatOrderRowDateTime()}</Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: "#fff",
            p: "16px 16px 8px 16px",
            width: 600,
            maxWidth: "95vw",
            boxSizing: "border-box",
            borderRadius: "8px",
          }}
        >
          <Grid container sx={{ width: "100%" }}>
            <Grid
              container
              item
              xs={3}
              justifyContent="flex-end"
              alignItems="center"
              sx={{ pr: 2, mb: 1 }}
            >
              <Typography variant="secondary" textAlign="right">
                {t("common:skills")}
              </Typography>
            </Grid>
            <Grid item xs={9} sx={{ mb: 1 }}>
              <CustomAutocompleteMulti
                disabled
                readOnly
                options={formatSkillsForAutocomplete(taskSpec.skillIds)}
                fullWidth
                name="skills"
                onChange={() => {}}
                onInputChange={() => {}}
                defaultValue={formatSkillsForAutocomplete(taskSpec.skillIds)}
                onOpen={() => {}}
              />
            </Grid>

            <Grid
              container
              item
              xs={3}
              justifyContent="flex-end"
              alignItems="center"
              sx={{ pr: 2, mb: 1 }}
            >
              <Typography variant="secondary" textAlign="right">
                {t("common:cardsAndCerts")}
              </Typography>
            </Grid>
            <Grid item xs={9} sx={{ mb: 1 }}>
              <CustomAutocompleteMulti
                disabled
                readOnly
                options={formatCertificatesForAutocomplete(
                  taskSpec.certificateIds,
                )}
                fullWidth
                name="certificates"
                onChange={() => {}}
                onInputChange={() => {}}
                defaultValue={formatCertificatesForAutocomplete(
                  taskSpec.certificateIds,
                )}
                onOpen={() => {}}
              />
            </Grid>

            <Grid
              container
              item
              xs={3}
              justifyContent="flex-end"
              alignItems="center"
              sx={{ pr: 2, mb: 1 }}
            >
              <Typography variant="secondary" textAlign="right">
                {t("common:trainings")}
              </Typography>
            </Grid>
            <Grid item xs={9} sx={{ mb: 1 }}>
              <CustomAutocompleteMulti
                disabled
                readOnly
                options={formatTrainingsForAutocomplete(taskSpec.trainings)}
                fullWidth
                name="trainings"
                onChange={() => {}}
                onInputChange={() => {}}
                defaultValue={formatTrainingsForAutocomplete(
                  taskSpec.trainings,
                )}
                onOpen={() => {}}
              />
            </Grid>

            <Grid
              container
              item
              xs={3}
              justifyContent="flex-end"
              alignItems="center"
              sx={{ pr: 2, mb: 2.5 }}
            >
              <Typography variant="secondary" textAlign="right">
                {t("common:languageSkills")}
              </Typography>
            </Grid>
            <Grid item xs={9} sx={{ mb: 2.5 }}>
              <CommonTextField
                disabled={true}
                fullWidth
                value={taskSpec.languageSkills
                  .map(
                    (skill) =>
                      `${getLanguageSkillsNameByCode(skill.languageCode)} (${t(`languageSkill:${skill.skillLevel}`)})`,
                  )
                  .join(", ")}
                size="small"
              />
            </Grid>

            <Grid
              container
              item
              xs={3}
              justifyContent="flex-end"
              alignItems="flex-start"
              sx={{ pr: 2, mb: 1 }}
            >
              <Typography variant="secondary" textAlign="right">
                {t("order:jobDescriptionFI")}
              </Typography>
            </Grid>
            <Grid item xs={9} sx={{ mb: 1 }}>
              <CommonTextField
                disabled={true}
                fullWidth
                multiline
                minRows={3}
                value={taskSpec.description || ""}
              />
            </Grid>

            <Grid
              container
              item
              xs={3}
              justifyContent="flex-end"
              alignItems="center"
              sx={{ pr: 2, mb: 1 }}
            >
              <Typography variant="secondary" textAlign="right">
                {t("order:arrivalInstructionsFI")}
              </Typography>
            </Grid>
            <Grid item xs={9} sx={{ mb: 1 }}>
              <CommonTextField
                disabled={true}
                fullWidth
                size="small"
                value={taskSpec.descriptionForTalent || ""}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Actions Banner */}
      <PopperActionBannerContainer>
        <Button
          sx={{ borderRadius: "20px" }}
          variant="contained"
          onClick={onClick}
          endIcon={
            <IconWrapper
              icon={ContinueIcon}
              sx={{
                "& path": { fill: "#fff" },
              }}
            />
          }
        >
          {t("order:viewOrder")}
        </Button>
      </PopperActionBannerContainer>
    </>
  );
};
